package com.advaita.cloud.drive.ui.api

import com.advaita.cloud.drive.shared.CreateFolderRequest
import com.advaita.cloud.drive.shared.PullRequest
import com.advaita.cloud.drive.shared.S3Credential
import com.advaita.cloud.drive.shared.UploadStartRequest
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

/**
 * ADV-1330 (the shared-folder create slice, scope 1): where a create goes. New folder, Upload
 * files, Upload folder, Pull from URL and the resume record all read it.
 *
 * - [MyDrive]: the caller's own folder at [MyDrive.path], sent as `parentPath`, as before.
 * - [SharedFolder]: a folder another person shared, sent as `parentId`. The new row belongs to
 *   the folder's owner, and the server checks `upload` on the folder (`CreationGate`).
 *
 * The two never mix: the backend answers `400` to a body with both `parentId` and `parentPath`.
 * Example: Lena opens Run 42 from Shared with me. Her upload targets `SharedFolder(Run 42)`, and
 * the zip lands in Kai's Run 42, not in Lena's own root.
 */
sealed interface CreateTarget {
    data class MyDrive(val path: String) : CreateTarget

    /** [name] is for the tips and the resume panel only. The server reads [folderId]. */
    data class SharedFolder(val folderId: String, val name: String) : CreateTarget
}

/** `POST /api/folders`: [target] as `parentPath` or as `parentId`, never both. */
internal fun createFolderBody(target: CreateTarget, name: String): CreateFolderRequest = when (target) {
    is CreateTarget.MyDrive -> CreateFolderRequest(parentPath = target.path, name = name)
    is CreateTarget.SharedFolder -> CreateFolderRequest(name = name, parentId = target.folderId)
}

/** `POST /api/uploads/multipart`. [relativePath] (Upload folder) works with either target. */
internal fun uploadStartBody(
    target: CreateTarget,
    name: String,
    mimeType: String?,
    size: Long,
    relativePath: String?,
): UploadStartRequest = when (target) {
    is CreateTarget.MyDrive -> UploadStartRequest(target.path, name, mimeType, size, relativePath)
    is CreateTarget.SharedFolder ->
        UploadStartRequest(name = name, mimeType = mimeType, size = size, relativePath = relativePath, parentId = target.folderId)
}

/** `POST /api/pull`. */
internal fun pullBody(target: CreateTarget, urls: List<String>, s3Credentials: List<S3Credential>): PullRequest = when (target) {
    is CreateTarget.MyDrive -> PullRequest(target.path, urls, s3Credentials)
    is CreateTarget.SharedFolder -> PullRequest(urls = urls, s3Credentials = s3Credentials, parentId = target.folderId)
}

/**
 * The JSON of a create body. A null field is left out, so a shared body names no `parentPath`
 * at all, and a My Drive body names no `parentId`.
 */
@OptIn(ExperimentalSerializationApi::class)
internal val createJson = Json {
    encodeDefaults = true
    explicitNulls = false
}

internal inline fun <reified T> encodeCreateBody(body: T): String = createJson.encodeToString(body)
